import React from 'react';
import { Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import useConfigStore from '../stores/configStore';
import MoodCheckin from '../components/MoodCheckin';

const { Text } = Typography;

// 协调的渐变色系：统一饱和度与明度，白色文字在所有卡片上都清晰
const modules = [
  { id: 'files', title: '文件管理', icon: '📁', gradient: 'linear-gradient(135deg,#5B8DEF,#3E6FE0)' },
  { id: 'items', title: '物品管理', icon: '🛒', gradient: 'linear-gradient(135deg,#35C4BE,#1FA39D)' },
  { id: 'finance', title: '资金管理', icon: '💰', gradient: 'linear-gradient(135deg,#F5A623,#EE8B00)' },
  { id: 'time', title: '时间管理', icon: '⏰', gradient: 'linear-gradient(135deg,#5AA9F0,#3D8AD9)' },
  { id: 'customers', title: '客户管理', icon: '👤', gradient: 'linear-gradient(135deg,#38BDF0,#1E9FD4)' },
  { id: 'emotions', title: '情绪管理', icon: '❤️', gradient: 'linear-gradient(135deg,#FF8686,#F76B6B)' },
  { id: 'body', title: '身材管理', icon: '🏆', gradient: 'linear-gradient(135deg,#56D4A8,#2FBB8A)' },
  { id: 'contacts', title: '人际管理', icon: '👥', gradient: 'linear-gradient(135deg,#A98AEB,#8A66E0)' },
  { id: 'study', title: '学习管理', icon: '📚', gradient: 'linear-gradient(135deg,#FBA94C,#F2902A)' },
  { id: 'projects', title: '项目管理', icon: '📊', gradient: 'linear-gradient(135deg,#5FCF80,#3BB65E)' },
  { id: 'health', title: '健康管理', icon: '💊', gradient: 'linear-gradient(135deg,#FB7A8E,#F1566E)' },
  { id: 'goals', title: '目标管理', icon: '🎯', gradient: 'linear-gradient(135deg,#B18AE8,#9566DE)' },
  { id: 'habits', title: '习惯管理', icon: '🔄', gradient: 'linear-gradient(135deg,#F6C445,#EDAA16)' },
  { id: 'creative', title: '创意管理', icon: '💡', gradient: 'linear-gradient(135deg,#FBA36B,#F5854A)' },
  { id: 'risk', title: '风险管理', icon: '⚠️', gradient: 'linear-gradient(135deg,#F19A8E,#E7796B)' },
  { id: 'environment', title: '环境管理', icon: '📍', gradient: 'linear-gradient(135deg,#5BD08A,#37B86B)' },
  { id: 'entertainment', title: '娱乐管理', icon: '▶️', gradient: 'linear-gradient(135deg,#F79B72,#F07E4E)' },
  { id: 'family', title: '家庭管理', icon: '🏠', gradient: 'linear-gradient(135deg,#E88AB0,#DE699A)' },
  { id: 'energy', title: '能量管理', icon: '⚡', gradient: 'linear-gradient(135deg,#4FB6F2,#2E97DB)' },
  { id: 'decisions', title: '决策管理', icon: '✅', gradient: 'linear-gradient(135deg,#63CE8E,#3FB86C)' },
];

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { membershipEnabled } = useConfigStore();

  const handleModuleClick = (module) => {
    // 检查用户是否已登录
    if (!user) {
      // 未登录用户，提示登录
      message.info('请先登录以使用完整功能');

      // 检测是否在小程序环境
      const isInMiniProgram = window.__wxjs_environment === 'miniprogram' ||
                             (window.wx && window.wx.miniProgram);

      if (isInMiniProgram) {
        // 在小程序环境，请求小程序发起登录
        console.log('在小程序环境，请求小程序发起登录');
        if (window.wx && window.wx.miniProgram) {
          window.wx.miniProgram.postMessage({
            data: { type: 'wechat_login_request' }
          });
        }
      } else {
        // 在网页环境，跳转到登录页面
        navigate('/login');
      }
      return;
    }

    // 权限检查函数
    const checkUserPermission = (user) => {
      // 会员付费关闭时（审核期），所有账号免费使用全部模块
      if (!membershipEnabled) {
        return true;
      }
      // 管理员始终有权限
      if (user?.role === 'admin') {
        return true;
      }

      // 检查是否是付费会员
      const paidMembers = ['VIP会员', '高级会员', '银卡会员', '金卡会员', '钻石会员'];
      if (paidMembers.includes(user?.member_level)) {
        return true;
      }

      // 检查试用期
      if (user?.trial_end_date) {
        const trialEndDate = new Date(user.trial_end_date);
        const now = new Date();
        return now <= trialEndDate;
      }

      return false;
    };

    // 检查用户权限
    const isVipUser = checkUserPermission(user);

    // 普通会员只能访问的模块
    const basicModules = ['files', 'items', 'finance', 'time'];

    // 如果用户不是VIP且尝试访问高级模块，显示高级会员专享提示并跳转到卡密商城
    if (!isVipUser && !basicModules.includes(module.id)) {
      // 检查是否在试用期内
      if (user?.trial_end_date) {
        const trialEndDate = new Date(user.trial_end_date);
        const now = new Date();
        if (now <= trialEndDate) {
          // 还在试用期内，允许访问
          navigate(`/${module.id}`);
          return;
        } else {
          // 试用期已过期
          message.info('试用期已过期，请升级为高级会员');
          setTimeout(() => {
            navigate('/card-sales');
          }, 1000);
          return;
        }
      } else {
        // 没有试用期，直接提示升级
        message.info('高级会员专享功能，正在跳转到卡密商城...');
        setTimeout(() => {
          navigate('/card-sales');
        }, 1000);
        return;
      }
    }

    // 根据模块ID进行导航
    switch (module.id) {
      case 'files':
        navigate('/files');
        break;
      case 'items':
        navigate('/items');
        break;
      case 'finance':
        navigate('/finance');
        break;
      case 'time':
        navigate('/time');
        break;
      case 'entertainment':
        navigate('/entertainment');
        break;
      case 'customers':
        navigate('/customers');
        break;
      case 'emotions':
        navigate('/emotions');
        break;
      case 'body':
        navigate('/body');
        break;
      case 'contacts':
        navigate('/contacts');
        break;
      case 'study':
        navigate('/study');
        break;
      case 'projects':
        navigate('/projects');
        break;
      case 'health':
        navigate('/health');
        break;
      case 'goals':
        navigate('/goals');
        break;
      case 'habits':
        navigate('/habits');
        break;
      case 'risk':
        navigate('/risk');
        break;
      case 'family':
        navigate('/family');
        break;
      case 'creative':
        navigate('/creative');
        break;
      case 'environment':
        navigate('/environment');
        break;
      case 'energy':
        navigate('/energy');
        break;
      case 'decisions':
        navigate('/decisions');
        break;
      default:
        // 其他模块暂时显示提示信息
        console.log('点击模块:', module.title);
        break;
    }
  };

  return (
    <div className="home-page">
      <MoodCheckin />
      <div className="home-container">
        {/* 顶部品牌 Hero */}
        <div className="home-hero">
          <div className="home-hero-title">管理师</div>
          <div className="home-hero-subtitle">综合管理 · 让生活井井有条</div>
        </div>

        {/* 模块宫格 - App 图标风格 */}
        <div className="module-grid">
          {modules.map((module) => (
            <div key={module.id} className="home-tile" onClick={() => handleModuleClick(module)}>
              <div className="home-tile-icon" style={{ background: module.gradient }}>{module.icon}</div>
              <div className="home-tile-label">{module.title}</div>
            </div>
          ))}
        </div>

        {/* 底部说明与备案 */}
        <div className="home-footer">
          <Text className="home-footer-tip">点击任意模块，开始管理你的生活与工作</Text>
          <div className="home-footer-links">
            <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">闽ICP备2025109602号-6</a>
            <a href="/terms">用户协议</a>
            <a href="/privacy">隐私政策</a>
            <a href="/billing">付费条款</a>
            <a href="/product">产品展示</a>
            <a href="/about">关于我们</a>
            <a href="/contact">联系我们</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
